import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowsRightLeftIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import {
    buildBreakdownRows,
    buildCurrencyBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelReport,
    sumBy,
} from "@/lib/export/excel";
import { getTransactions } from "../api/walletPaymentsApi";
import type { TransactionsQueryParams, TransactionDirection, TransactionType, TransactionStatus } from "../types";

// ============================================================================
// Constants
// ============================================================================

const directionOptions: { value: TransactionDirection | ""; label: string }[] = [
    { value: "", label: "All Directions" },
    { value: "CREDIT", label: "Credit" },
    { value: "DEBIT", label: "Debit" },
];

const typeOptions: { value: TransactionType | ""; label: string }[] = [
    { value: "", label: "All Types" },
    { value: "TOP_UP", label: "Top Up" },
    { value: "WITHDRAWAL", label: "Withdrawal" },
    { value: "RIDE_PAYMENT", label: "Ride Payment" },
    { value: "RIDE_EARNING", label: "Ride Earning" },
    { value: "COMMISSION", label: "Commission" },
    { value: "REFUND", label: "Refund" },
    { value: "ADMIN_CREDIT", label: "Admin Credit" },
    { value: "ADMIN_DEBIT", label: "Admin Debit" },
];

const statusOptions: { value: TransactionStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "COMPLETED", label: "Completed" },
    { value: "FAILED", label: "Failed" },
    { value: "REVERSED", label: "Reversed" },
];

// ============================================================================
// Badges
// ============================================================================

const DirectionBadge = ({ direction }: { direction: string }) => {
    return direction === "CREDIT" ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <ArrowUpIcon className="h-3 w-3" /> CREDIT
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
            <ArrowDownIcon className="h-3 w-3" /> DEBIT
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        REVERSED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const TransactionsRoute = () => {
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [filters, setFilters] = useState<TransactionsQueryParams>({
        page: 0,
        size: 20,
        direction: "",
        type: "",
        status: "",
        search: "",
        dateFrom: "",
        dateTo: "",
    });
    const [showFilters, setShowFilters] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ["transactions", filters],
        queryFn: () => getTransactions(filters),
    });

    const transactions = data?.data?.transactions ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");

            const transactionRows = await collectPaginatedRows({
                fetchPage: (page) =>
                    getTransactions({
                        ...filters,
                        page,
                        size: 100,
                    }),
                getRows: (response) => response.data.transactions,
                getTotalPages: (response) => response.data.totalPages,
            });

            const directionCounts = countBy(transactionRows, (transaction) => transaction.direction);
            const statusCounts = countBy(transactionRows, (transaction) => transaction.status);
            const typeCounts = countBy(transactionRows, (transaction) => transaction.type);
            const totalCredits = sumBy(transactionRows, (transaction) =>
                transaction.direction === "CREDIT" ? transaction.amount : 0
            );
            const totalDebits = sumBy(transactionRows, (transaction) =>
                transaction.direction === "DEBIT" ? transaction.amount : 0
            );

            await exportRide2FiveExcelReport({
                title: "Transactions Export",
                subtitle:
                    "Filtered wallet transaction export with flow, status, and type analysis.",
                fileName: createExportFileName("transactions_export"),
                filters,
                rows: transactionRows,
                metrics: [
                    { label: "Filtered Transactions", value: transactionRows.length },
                    {
                        label: "Total Credits",
                        value: totalCredits.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                    {
                        label: "Total Debits",
                        value: totalDebits.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                    {
                        label: "Net Flow",
                        value: (totalCredits - totalDebits).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                ],
                sections: [
                    {
                        title: "Direction Breakdown",
                        rows: buildBreakdownRows(directionCounts),
                    },
                    {
                        title: "Status Breakdown",
                        rows: buildBreakdownRows(statusCounts),
                    },
                    {
                        title: "Transaction Type Breakdown",
                        rows: buildBreakdownRows(typeCounts),
                    },
                    {
                        title: "Amount By Currency",
                        rows: buildCurrencyBreakdownRows(
                            transactionRows,
                            (transaction) => transaction.currency,
                            (transaction) => transaction.amount
                        ),
                    },
                ],
                columns: [
                    {
                        header: "Transaction ID",
                        width: 38,
                        value: (transaction) => transaction.transactionId,
                    },
                    {
                        header: "Wallet ID",
                        width: 38,
                        value: (transaction) => transaction.walletId,
                    },
                    {
                        header: "User ID",
                        width: 38,
                        value: (transaction) => transaction.userId,
                    },
                    {
                        header: "First Name",
                        width: 18,
                        value: (transaction) => transaction.firstName,
                    },
                    {
                        header: "Last Name",
                        width: 18,
                        value: (transaction) => transaction.lastName,
                    },
                    { header: "Email", width: 28, value: (transaction) => transaction.email },
                    { header: "Direction", width: 12, value: (transaction) => transaction.direction },
                    { header: "Type", width: 18, value: (transaction) => transaction.type },
                    { header: "Status", width: 14, value: (transaction) => transaction.status },
                    {
                        header: "Amount",
                        width: 16,
                        alignment: "right",
                        value: (transaction) => transaction.amount,
                        numFmt: "#,##0.00",
                    },
                    { header: "Currency", width: 12, value: (transaction) => transaction.currency },
                    { header: "Reference", width: 24, value: (transaction) => transaction.reference },
                    {
                        header: "Description",
                        width: 32,
                        value: (transaction) => transaction.description,
                    },
                    {
                        header: "Processed At",
                        width: 22,
                        value: (transaction) =>
                            transaction.processedAt
                                ? formatDate(transaction.processedAt)
                                : "—",
                    },
                    {
                        header: "Created At",
                        width: 22,
                        value: (transaction) => formatDate(transaction.createdAt),
                    },
                ],
                dataSheetName: "Transactions",
            });
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by reference or description…"
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 0 }))}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary-500 dark:focus:ring-primary-900/30"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                        <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                    </Button>
                    <Button size="sm" onClick={handleExport} disabled={isExporting}>
                        <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                        {isExporting ? "Exporting..." : "Export Excel"}
                    </Button>
                </div>
            </div>

            {exportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                    {exportError}
                </div>
            ) : null}

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Direction</label>
                            <select
                                value={filters.direction}
                                onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value as TransactionDirection | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {directionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Type</label>
                            <select
                                value={filters.type}
                                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value as TransactionType | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as TransactionStatus | "", page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            >
                                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Date From</label>
                            <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value, page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Date To</label>
                            <input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value, page: 0 }))}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setFilters({ page: 0, size: 20, direction: "", type: "", status: "", search: "", dateFrom: "", dateTo: "" })}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
                    </div>
                ) : error ? (
                    <div className="py-20 text-center text-sm text-rose-500">{String(error)}</div>
                ) : transactions.length === 0 ? (
                    <div className="py-20 text-center">
                        <ArrowsRightLeftIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No transactions found.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">User</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Type</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Direction</th>
                                    <th className="px-4 py-3 text-right font-medium text-slate-500 dark:text-slate-400">Amount</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Reference</th>
                                    <th className="px-4 py-3 text-center font-medium text-slate-500 dark:text-slate-400">Status</th>
                                    <th className="px-4 py-3 text-left font-medium text-slate-500 dark:text-slate-400">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {transactions.map((tx) => (
                                    <tr
                                        key={tx.transactionId}
                                        onClick={() => navigate(`/wallet-payments/transactions/${tx.transactionId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                                                    <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900 dark:text-white">
                                                        {tx.firstName} {tx.lastName}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">{tx.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                {tx.type?.replace(/_/g, " ") ?? "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <DirectionBadge direction={tx.direction} />
                                        </td>
                                        <td className={`px-4 py-3 text-right font-semibold ${tx.direction === "CREDIT" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                                            {tx.direction === "CREDIT" ? "+" : "−"}{formatCurrency(tx.amount, tx.currency)}
                                        </td>
                                        <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                                            {tx.reference || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <StatusBadge status={tx.status} />
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                                            {formatDate(tx.processedAt || tx.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 dark:border-slate-800">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Page {currentPage + 1} of {totalPages} · {totalElements} transactions
                        </p>
                        <div className="flex gap-1">
                            <Button variant="secondary" size="sm" onClick={() => setFilters((f) => ({ ...f, page: currentPage - 1 }))} disabled={currentPage === 0}>
                                <ChevronLeftIcon className="h-4 w-4" />
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => setFilters((f) => ({ ...f, page: currentPage + 1 }))} disabled={currentPage >= totalPages - 1}>
                                <ChevronRightIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
